import React, { useState } from 'react';
import { apiClient, getErrorMessage } from '../packages/api-client';

type Item = { id: string; name: string; selected: boolean };
type Capacity = { projects: Item[]; people: Item[]; owner_id?: string };

export function RetainedCapacity() {
    const [data, setData] = useState<Capacity | null>(null);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [busy, setBusy] = useState(false);

    async function load() {
        setBusy(true);
        setError('');
        try {
            const response = await apiClient.get<Capacity>('/billing/capacity');
            setData(response.data);
        } catch (err) { setError(getErrorMessage(err)); }
        finally { setBusy(false); }
    }

    async function save() {
        if (!data) return;
        setBusy(true);
        setError('');
        try {
            await apiClient.put('/billing/capacity', {
                projects: data.projects.filter(x => x.selected).map(x => x.id),
                people: data.people.filter(x => x.selected || x.id === data.owner_id).map(x => x.id),
            });
            setSaved(true);
            const response = await apiClient.get<Capacity>('/billing/capacity');
            setData(response.data);
        } catch (err) { setError(getErrorMessage(err)); }
        finally { setBusy(false); }
    }

    return (
        <section className="hub-panel">
            <h2>Keep working after a downgrade</h2>
            <p>Choose the projects and people to keep within your current plan. Other projects and excess members become read-only; data is preserved. Unselected slots are filled by the oldest projects and members, with the owner first. Archive projects or deactivate members to free capacity.</p>
            <button className="btn btn-secondary" onClick={load} disabled={busy}>Choose retained projects and people</button>
            {data && <>
                {(['projects', 'people'] as const).map(kind => (
                    <fieldset key={kind}>
                        <legend>{kind === 'projects' ? 'Projects' : 'People'}</legend>
                        {data[kind].map(item => (
                            <label style={{ display: 'block' }} key={item.id}>
                                <input type="checkbox"
                                    checked={item.selected || (kind === 'people' && item.id === data.owner_id)}
                                    disabled={busy || (kind === 'people' && item.id === data.owner_id)}
                                    onChange={event => {
                                        setSaved(false);
                                        setData({ ...data, [kind]: data[kind].map(x => x.id === item.id ? { ...x, selected: event.target.checked } : x) });
                                    }} /> {item.name}
                            </label>
                        ))}
                    </fieldset>
                ))}
                <button className="btn btn-primary" disabled={busy} onClick={save}>Save retained access</button>
            </>}
            {error && <p role="alert">{error}</p>}
            {saved && <p role="status">Retained access saved.</p>}
        </section>
    );
}
